import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Alert, Button, Space, Tag } from 'antd';
import { CloudDownloadOutlined, SyncOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';

/**
 * One line for the accountants: when the client's Drive was last read and
 * what it brought, with „fetch now". Nothing shows for a company whose Drive
 * is not set up; that is done in tech-admin.
 */
export default function DriveStatusLine({ company, onImported }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();

  const { data: s } = useQuery({
    queryKey: ['tax-returns-drive', company],
    queryFn: () => API.taxReturns.getDriveStatus(company),
    enabled: Boolean(company),
    refetchInterval: (q) => (q.state.data?.lastRun?.running ? 4000 : false),
  });
  const sync = useMutation({
    mutationFn: () => API.taxReturns.startDriveSync(company),
    onSuccess: (data) => queryClient.setQueryData(['tax-returns-drive', company], data),
  });

  // When a run ends, the list is loaded again.
  const running = Boolean(s?.lastRun?.running);
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) onImported?.();
    wasRunning.current = running;
  }, [running, onImported]);

  if (!s?.connected || !s.rootId) return null;
  const last = s.lastRun;
  const problems = (last?.errors || 0) + (last?.unmapped || 0);

  const text = running
    ? t('drive.line.running')
    : last?.finishedAt
      ? t('drive.line.last', {
          at: dayjs(last.finishedAt).format('DD.MM.YYYY HH:mm'),
          returns: last.created || 0,
          statements: last.statements || 0,
        })
      : t('drive.line.never');

  return (
    <Alert
      type={last?.ok === false ? 'error' : 'info'}
      showIcon
      icon={running ? <SyncOutlined spin /> : <CloudDownloadOutlined />}
      style={{ marginBottom: 16 }}
      message={
        <Space wrap size={6}>
          <span>{text}</span>
          {last?.ok === false && !running && <Tag color="red">{t('drive.failed')}</Tag>}
          {problems > 0 && !running && <Tag color="orange">{t('drive.line.problems', { count: problems })}</Tag>}
        </Space>
      }
      action={
        <Button shape="round" size="small" disabled={running} loading={sync.isPending} onClick={() => sync.mutate()}>
          {t('drive.fetchNow')}
        </Button>
      }
    />
  );
}
